/**
 * @file api-impersonation.ts
 * @description Клиент для endpoint'ов impersonation (phase06).
 *
 *   POST /admin/impersonation/start         — start({ targetUserId, ... })
 *   POST /admin/impersonation/stop          — stop()
 *   POST /admin/impersonation/switch-mode   — switchMode({ mode, reason? })
 */

import { apiFetch } from "./api-client";

export type ImpersonationMode = "VIEW" | "ACT";

export interface ActiveImpersonation {
  sessionId: string;
  mode: ImpersonationMode;
  reason: string | null;
  startedAt: string;
  impersonator: {
    id: string;
    email: string;
    displayName: string | null;
    roleSnapshot: string;
  };
  target: {
    id: string;
    email: string;
    displayName: string | null;
    contextId: string;
  };
}

export interface StartImpersonationInput {
  targetUserId: string;
  targetContextId: string;
  mode: ImpersonationMode;
  reason?: string;
}

export async function startImpersonation(
  input: StartImpersonationInput,
): Promise<ActiveImpersonation> {
  return apiFetch<ActiveImpersonation>("/admin/impersonation/start", {
    method: "POST",
    json: input,
  });
}

export async function stopImpersonation(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/admin/impersonation/stop", { method: "POST" });
}

export async function switchImpersonationMode(input: {
  mode: ImpersonationMode;
  reason?: string;
}): Promise<ActiveImpersonation> {
  return apiFetch<ActiveImpersonation>("/admin/impersonation/switch-mode", {
    method: "POST",
    json: input,
  });
}

/**
 * Получить текущую активную impersonation-сессию impersonator'а (если есть).
 * Используется UI для определения «висящей» сессии при открытии «Войти как».
 */
export async function getActiveImpersonation(): Promise<ActiveImpersonation | null> {
  return apiFetch<ActiveImpersonation | null>("/admin/impersonation/active");
}

/**
 * Подключиться к существующей «висящей» сессии — записать её id в текущую
 * cookie. После вызова — reload, чтобы AuthProvider перечитал /me.
 */
export async function adoptImpersonation(
  sessionId: string,
): Promise<ActiveImpersonation> {
  return apiFetch<ActiveImpersonation>(
    `/admin/impersonation/adopt/${sessionId}`,
    { method: "POST" },
  );
}
